export type TextDirection = 'ltr' | 'rtl'
export type ScrollPosition = 'start' | 'middle' | 'end'
export type RtlScrollType = 'default' | 'negative' | 'reverse'
export type PhysicalHorizontalDirection = 'left' | 'right'
export type LogicalHorizontalDirection = 'previous' | 'next'

const SCROLL_POSITION_TOLERANCE = 1

let cachedRtlScrollType: RtlScrollType | undefined

function clampScrollLeft(scrollLeft: number, maxScrollLeft: number): number {
  return Math.min(Math.max(scrollLeft, 0), maxScrollLeft)
}

export function getMaxHorizontalScroll(element: HTMLElement): number {
  return Math.max(0, element.scrollWidth - element.clientWidth)
}

export function getElementTextDirection(element: HTMLElement): TextDirection {
  if (typeof window === 'undefined' || !window.getComputedStyle) {
    return element.dir === 'rtl' ? 'rtl' : 'ltr'
  }

  return window.getComputedStyle(element).direction === 'rtl' ? 'rtl' : 'ltr'
}

export function getRtlScrollType(): RtlScrollType {
  if (cachedRtlScrollType) return cachedRtlScrollType

  if (typeof document === 'undefined' || !document.body) {
    return 'negative'
  }

  const outer = document.createElement('div')
  const inner = document.createElement('div')

  outer.dir = 'rtl'
  outer.style.width = '4px'
  outer.style.height = '1px'
  outer.style.overflow = 'scroll'
  outer.style.position = 'absolute'
  outer.style.top = '-9999px'

  inner.style.width = '8px'
  inner.style.height = '1px'

  outer.appendChild(inner)
  document.body.appendChild(outer)

  if (outer.scrollLeft > 0) {
    cachedRtlScrollType = 'default'
  } else {
    outer.scrollLeft = 1
    cachedRtlScrollType = outer.scrollLeft === 0 ? 'negative' : 'reverse'
  }

  document.body.removeChild(outer)

  return cachedRtlScrollType
}

export function convertPhysicalScrollLeftToLogical(
  physicalScrollLeft: number,
  maxScrollLeft: number,
  textDirection: TextDirection,
  rtlScrollType: RtlScrollType = getRtlScrollType(),
): number {
  if (textDirection === 'ltr') {
    return clampScrollLeft(physicalScrollLeft, maxScrollLeft)
  }

  if (rtlScrollType === 'negative') {
    return clampScrollLeft(-physicalScrollLeft, maxScrollLeft)
  }

  if (rtlScrollType === 'default') {
    return clampScrollLeft(maxScrollLeft - physicalScrollLeft, maxScrollLeft)
  }

  return clampScrollLeft(physicalScrollLeft, maxScrollLeft)
}

export function convertLogicalScrollLeftToPhysical(
  logicalScrollLeft: number,
  maxScrollLeft: number,
  textDirection: TextDirection,
  rtlScrollType: RtlScrollType = getRtlScrollType(),
): number {
  const clampedScrollLeft = clampScrollLeft(logicalScrollLeft, maxScrollLeft)

  if (textDirection === 'ltr') {
    return clampedScrollLeft
  }

  if (rtlScrollType === 'negative') {
    return clampedScrollLeft === 0 ? 0 : -clampedScrollLeft
  }

  if (rtlScrollType === 'default') {
    return maxScrollLeft - clampedScrollLeft
  }

  return clampedScrollLeft
}

export function getLogicalScrollLeft(element: HTMLElement): number {
  return convertPhysicalScrollLeftToLogical(
    element.scrollLeft,
    getMaxHorizontalScroll(element),
    getElementTextDirection(element),
  )
}

export function setLogicalScrollLeft(element: HTMLElement, logicalScrollLeft: number) {
  element.scrollLeft = convertLogicalScrollLeftToPhysical(
    logicalScrollLeft,
    getMaxHorizontalScroll(element),
    getElementTextDirection(element),
  )
}

export function scrollToLogicalPosition(
  element: HTMLElement,
  options: Pick<ScrollToOptions, 'behavior' | 'left'>,
) {
  const left = convertLogicalScrollLeftToPhysical(
    options.left ?? getLogicalScrollLeft(element),
    getMaxHorizontalScroll(element),
    getElementTextDirection(element),
  )

  if (typeof element.scrollTo === 'function') {
    element.scrollTo({
      left,
      behavior: options.behavior,
    })
    return
  }

  element.scrollLeft = left
}

export function scrollByLogicalPosition(
  element: HTMLElement,
  options: Pick<ScrollToOptions, 'behavior' | 'left'>,
) {
  scrollToLogicalPosition(element, {
    left: getLogicalScrollLeft(element) + (options.left ?? 0),
    behavior: options.behavior,
  })
}

export function getLogicalHorizontalScrollPosition(
  element: HTMLElement,
  nextLogicalScrollLeft?: number,
): ScrollPosition {
  const maxScrollLeft = getMaxHorizontalScroll(element)
  const logicalScrollLeft = clampScrollLeft(nextLogicalScrollLeft ?? getLogicalScrollLeft(element), maxScrollLeft)

  if (logicalScrollLeft <= SCROLL_POSITION_TOLERANCE) {
    return 'start'
  }

  if (logicalScrollLeft >= maxScrollLeft - SCROLL_POSITION_TOLERANCE) {
    return 'end'
  }

  return 'middle'
}

export function getLogicalDirectionForPhysicalHorizontalDirection(
  textDirection: TextDirection,
  physicalDirection: PhysicalHorizontalDirection,
): LogicalHorizontalDirection {
  if (textDirection === 'rtl') {
    return physicalDirection === 'left' ? 'next' : 'previous'
  }

  return physicalDirection === 'left' ? 'previous' : 'next'
}

export function getLogicalScrollDeltaForPhysicalHorizontalDirection(
  textDirection: TextDirection,
  physicalDirection: PhysicalHorizontalDirection,
  amount: number,
): number {
  return getLogicalDirectionForPhysicalHorizontalDirection(textDirection, physicalDirection) === 'next' ?
    amount :
      -amount
}

export function isPhysicalHorizontalArrowHidden(
  scrollPosition: ScrollPosition,
  textDirection: TextDirection,
  physicalDirection: PhysicalHorizontalDirection,
): boolean {
  const logicalDirection = getLogicalDirectionForPhysicalHorizontalDirection(textDirection, physicalDirection)
  return (logicalDirection === 'previous' && scrollPosition === 'start') ||
    (logicalDirection === 'next' && scrollPosition === 'end')
}

// Returns a position in a virtual coordinate space where inline-start < inline-end,
// regardless of text direction. Only meaningful for computing deltas between two elements.
export function getLogicalInlineStartPosition(
  element: HTMLElement,
  textDirection: TextDirection = getElementTextDirection(element),
): number {
  const rect = element.getBoundingClientRect()
  return textDirection === 'rtl' ? -rect.right : rect.left
}

export function getLogicalInlineEndPosition(
  element: HTMLElement,
  textDirection: TextDirection = getElementTextDirection(element),
): number {
  const rect = element.getBoundingClientRect()
  return textDirection === 'rtl' ? -rect.left : rect.right
}

export function getLogicalInlineCenterPosition(
  element: HTMLElement,
  textDirection: TextDirection = getElementTextDirection(element),
): number {
  return (getLogicalInlineStartPosition(element, textDirection) + getLogicalInlineEndPosition(element, textDirection)) / 2
}

export function getElementLogicalInlineStartOffset(
  element: HTMLElement,
  scrollElement: HTMLElement,
  textDirection: TextDirection = getElementTextDirection(scrollElement),
): number {
  return getLogicalScrollLeft(scrollElement) +
    getLogicalInlineStartPosition(element, textDirection) -
    getLogicalInlineStartPosition(scrollElement, textDirection)
}
